import { useEffect, useRef, useState } from 'react';
import { DEFAULT_MAX_VISIBLE_TOASTS, MIN_VISIBLE_TOASTS, SPACING, ToasterProps } from './Toaster.types';
import { ToasterElement } from './ToasterElement';
import { getElementBoundingBox } from '../../utils/getElementBoundingBox';
import { useToasterScroller } from './useToasterScroller';

export const Toaster: React.FC<ToasterProps> = ({ toasts, onToastRemove }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRefs = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const [maxVisibleToasts, setMaxVisibleToasts] = useState(DEFAULT_MAX_VISIBLE_TOASTS);
  const { numOfScrolledToasts, decreaseScrolledToasts } = useToasterScroller(
    containerRef,
    toasts.length,
    maxVisibleToasts,
  );

  useEffect(() => {
    if (!containerRef.current) return;

    const calculateMaxVisibleToasts = (): void => {
      const availableHeight = getElementBoundingBox(containerRef.current).height;
      const maxToasts = Math.floor(availableHeight / SPACING);
      setMaxVisibleToasts(Math.max(MIN_VISIBLE_TOASTS, maxToasts));
    };

    calculateMaxVisibleToasts();

    const resizeObserver = new ResizeObserver(() => {
      calculateMaxVisibleToasts();
    });

    resizeObserver.observe(document.body);

    // eslint-disable-next-line consistent-return
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const removeToast = (toastId: string): void => {
    const timeout = timeoutRefs.current.get(toastId);
    if (timeout) {
      clearTimeout(timeout);
      timeoutRefs.current.delete(toastId);
    }

    onToastRemove?.(toastId);
    decreaseScrolledToasts();
  };

  useEffect(() => {
    toasts.forEach((toast) => {
      if (toast.timeout && !timeoutRefs.current.has(toast.id)) {
        timeoutRefs.current.set(
          toast.id,
          setTimeout(() => {
            removeToast(toast.id);
          }, toast.timeout),
        );
      }
    });

    return () => {
      timeoutRefs.current.forEach((timeout) => {
        clearTimeout(timeout);
      });
      timeoutRefs.current.clear();
    };
  }, [toasts]);

  return (
    <div className="fixed right-4 bottom-16 top-4 w-125 pointer-events-none z-60" ref={containerRef}>
      <div className="pointer-events-auto">
        {toasts.map((toast, index) => (
          <ToasterElement
            key={`container_${toast.id}`}
            index={index - numOfScrolledToasts}
            toast={toast}
            maxVisibleToasts={maxVisibleToasts}
            onRemove={removeToast}
          />
        ))}
      </div>
    </div>
  );
};
